import React, { useState, useEffect } from "react";
import { toast, Toaster } from "sonner";
import confetti from "canvas-confetti";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api from "../lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Celebration: golden confetti + burgundy petals for a warm, refined burst.
const celebrate = () => {
  const gold = ["#B08D57", "#D4B98A", "#E9DDC7"];
  const burg = ["#7A2E2E", "#B08D57", "#FBF7F1"];
  // Three staggered bursts from left, right, top-center
  confetti({ particleCount: 90, spread: 70, origin: { x: 0.1, y: 0.6 }, colors: gold, ticks: 240, scalar: 1.1 });
  confetti({ particleCount: 90, spread: 70, origin: { x: 0.9, y: 0.6 }, colors: burg, ticks: 240, scalar: 1.1 });
  setTimeout(() => confetti({ particleCount: 140, spread: 100, startVelocity: 45, origin: { x: 0.5, y: 0.2 }, colors: [...gold, ...burg], ticks: 300, scalar: 1.2 }), 250);
  setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, colors: gold }), 500);
  setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, colors: gold }), 500);
};

const Field = ({ label, required, children, hint, testId }) => (
  <div className="space-y-2" data-testid={testId}>
    <Label className="text-[10px] tracking-[0.3em] uppercase text-gold flex items-baseline gap-1">
      {label} {required && <span className="text-burgundy/70">*</span>}
    </Label>
    {children}
    {hint && <div className="text-xs text-ink-faint">{hint}</div>}
  </div>
);

const inputCls =
  "bg-transparent border-0 border-b border-hairline rounded-none px-0 py-3 h-auto text-base text-ink placeholder:text-ink-faint focus-visible:ring-0 focus-visible:border-gold transition-colors";

const Rsvp = ({ wedding = "traditional" }) => {
  const isWhite = wedding === "white";
  const eventLabel = isWhite ? "White Wedding" : "Traditional Wedding";
  const eventCopy = isWhite
    ? "Kindly confirm your attendance for Anjola & Michael's White Wedding Celebration."
    : "Kindly confirm your attendance for Anjola & Michael's Traditional Wedding Ceremony on May 8, 2027.";

  const [form, setForm] = useState({
    email: "", full_name: "", phone: "",
    attending: "",
    dietary: "None", dietary_note: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [errors, setErrors] = useState({});

  // Dietary is collected only for the White Wedding; Traditional does not ask.
  const askDietary = isWhite;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!EMAIL_RE.test(form.email.trim())) e.email = "Please enter a valid email address (e.g. name@example.com).";
    if (!form.full_name.trim()) e.full_name = "Full name is required.";
    if (!form.phone.trim()) e.phone = "Phone / WhatsApp is required.";
    if (!form.attending) e.attending = "Please choose an option.";
    if (askDietary && !form.dietary) e.dietary = "Please select a dietary option.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Please complete all required fields."); return; }
    setSubmitting(true);
    try {
      const payload = {
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        attending: form.attending,
        bringing_guest: "no",
        guest_name: null,
        guest_email: null,
        dietary: askDietary ? form.dietary : "None",
        dietary_note: askDietary && form.dietary === "Other" ? form.dietary_note.trim() : null,
        message: form.message.trim() || null,
        wedding,
      };
      const { data } = await api.post("/rsvp", payload);
      setConfirmed(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please review the form.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return <ConfirmationScreen confirmed={confirmed} eventLabel={eventLabel} />;
  }

  return (
    <div data-testid="rsvp-page" className="max-w-2xl mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-24">
      <Toaster richColors position="top-center" />
      <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-6">{eventLabel}</div>
      <h1 className="font-serif text-5xl sm:text-6xl leading-[0.95] tracking-tighter text-ink">RSVP</h1>
      <p className="mt-6 text-base sm:text-lg text-ink-soft leading-relaxed">
        {eventCopy}
      </p>

      <div
        data-testid="rsvp-deadline"
        className="mt-8 inline-flex items-center gap-3 border border-gold/60 bg-cream/60 px-5 py-3"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Kindly RSVP by</span>
        <span className="font-serif text-xl text-ink leading-none">January 8, 2027</span>
      </div>

      <p className="mt-4 text-sm text-ink-soft leading-relaxed">
        Your early response helps us thoughtfully plan a beautiful and memorable experience for all our guests.
      </p>

      <form onSubmit={onSubmit} className="mt-14 space-y-10" data-testid="rsvp-form" noValidate>
        <Field label="Email Address" required testId="field-email" hint={errors.email}>
          <Input data-testid="rsvp-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
            placeholder="name@example.com" className={inputCls} />
        </Field>

        <Field label="Full Name" required testId="field-name" hint={errors.full_name}>
          <Input data-testid="rsvp-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
            placeholder="Your full name" className={inputCls} />
        </Field>

        <Field label="Phone / WhatsApp" required testId="field-phone" hint={errors.phone}>
          <Input data-testid="rsvp-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
            placeholder="+000 000 0000" className={inputCls} />
        </Field>

        <Field label="Will you be attending?" required testId="field-attending" hint={errors.attending}>
          <RadioGroup value={form.attending} onValueChange={(v) => set("attending", v)} className="grid sm:grid-cols-2 gap-3 pt-2">
            <label data-testid="rsvp-attending-yes" className={`cursor-pointer border px-5 py-4 flex items-center gap-3 ${form.attending === "yes" ? "border-gold bg-cream/50" : "border-hairline"}`}>
              <RadioGroupItem value="yes" id="att-yes" className="text-gold" />
              <span className="text-sm text-ink">Yes, I will be there</span>
            </label>
            <label data-testid="rsvp-attending-no" className={`cursor-pointer border px-5 py-4 flex items-center gap-3 ${form.attending === "no" ? "border-gold bg-cream/50" : "border-hairline"}`}>
              <RadioGroupItem value="no" id="att-no" className="text-gold" />
              <span className="text-sm text-ink">Sorry, I cannot make it</span>
            </label>
          </RadioGroup>
        </Field>

        {askDietary && (
          <Field label="Dietary Restrictions" required testId="field-dietary" hint={errors.dietary}>
            <Select value={form.dietary} onValueChange={(v) => set("dietary", v)}>
              <SelectTrigger data-testid="rsvp-dietary" className={`${inputCls} justify-between`}>
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent className="bg-ivory border-hairline">
                {["None", "Vegetarian", "Vegan", "Halal", "Other"].map((o) => (
                  <SelectItem key={o} value={o} data-testid={`diet-${o.toLowerCase()}`}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}

        {askDietary && form.dietary === "Other" && (
          <Field label="Please specify" testId="field-dietary-note">
            <Input data-testid="rsvp-dietary-note" value={form.dietary_note} onChange={(e) => set("dietary_note", e.target.value)}
              placeholder="Tell us more" className={inputCls} />
          </Field>
        )}

        <Field label="Message for the couple (optional)" testId="field-message">
          <Textarea data-testid="rsvp-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)}
            placeholder="A note of love, prayer, or well-wishes"
            className="bg-transparent border border-hairline rounded-none focus-visible:ring-0 focus-visible:border-gold text-ink" />
        </Field>

        <div className="pt-4">
          <button
            type="submit"
            disabled={submitting}
            data-testid="rsvp-submit-btn"
            className="btn-gold w-full sm:w-auto inline-flex items-center justify-center border border-gold text-gold text-[11px] tracking-[0.25em] uppercase px-10 py-4 rounded-full disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Submit RSVP"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Rsvp;

/** Confirmation screen with a warm confetti burst on mount. */
const ConfirmationScreen = ({ confirmed, eventLabel }) => {
  useEffect(() => {
    celebrate();
    const t = setTimeout(celebrate, 900);
    return () => clearTimeout(t);
  }, []);
  return (
    <div data-testid="rsvp-confirmation" className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center animate-fade-up">
      <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-6">RSVP Received</div>
      <h1 className="font-serif text-5xl sm:text-6xl leading-[0.95] tracking-tighter text-ink">
        Thank you for your RSVP.
      </h1>
      <div className="hairline my-10 justify-center"><span>A · M</span></div>
      <div className="text-ink-soft leading-[1.9] text-base sm:text-lg space-y-5">
        <p>We have received your response for <span className="font-serif italic text-ink">Anjola &amp; Michael&rsquo;s</span> {eventLabel}.</p>
        <p>We look forward to celebrating with you.</p>
        <p className="pt-4 font-serif italic text-ink">With love,</p>
        <p className="text-gold text-sm tracking-[0.2em] uppercase">The Temowo &amp; Itemuagbor Families</p>
      </div>
      <div className="mt-10 text-xs text-ink-faint">
        A confirmation email has been sent to <span className="text-ink">{confirmed.email}</span>.
      </div>
    </div>
  );
};
